import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, Grid, TextField, Autocomplete } from "@mui/material";
import { Formik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import SubmitModalWithFormik from "../../../components/common/modalPopup/SubmitModalWithFormik";
import Loader from "../../../components/common/loader/Loader";
import getAxiosError from "../../../app/api/axiosError";
import { ClientInvoiceAgent } from "../../../app/api/clientInvoices/ClientInvoiceAgent";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import FormikTextField from "../../../components/common/formikFields/FormikTextField";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { fetchStoreRateCardAssociationList } from "../../../app/features/ratecard/ratecardSlice";
import { getFdpListByClient } from "../../../app/features/fdp/fdpSlice";
import { SubmittedClientInvoice } from "../../../app/models/responseModels/ClientInvoiceResponses";

interface ManualUploadSubmittedInvoiceModalProps {
  clientId: number;
  closeManualUploadSubmittedInvoiceModal: CallableFunction;
  onCompleted: CallableFunction;
}

interface ManualUploadSubmittedInvoiceUiRequest {
  file: File | null;
  invoiceId: string;
  status: string;
  associatedStores: string[];
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  submitDate: Dayjs | null;
  associatedFdps: string[];
  netPayment: number | "";
}

const ManualUploadSubmittedInvoiceModal = (
  props: ManualUploadSubmittedInvoiceModalProps
) => {
  const initialValues: ManualUploadSubmittedInvoiceUiRequest = {
    file: null,
    invoiceId: "",
    status: "Client Invoiced",
    associatedStores: [],
    startDate: null,
    endDate: null,
    submitDate: null,
    associatedFdps: [],
    netPayment: "",
  };

  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [fileError, setFileError] = useState<boolean>(false);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState<boolean>(false);
  const [pendingValues, setPendingValues] = useState<ManualUploadSubmittedInvoiceUiRequest | null>(null);
  const [existingInvoices, setExistingInvoices] = useState<SubmittedClientInvoice[]>([]);
  const [validationErrorMsg, setValidationErrorMsg] = useState<string>("");

  useEffect(() => {
    dispatch(fetchStoreRateCardAssociationList(props.clientId));
    dispatch(getFdpListByClient(props.clientId));
  }, [dispatch, props.clientId]);

  // Get stores and FDPs from Redux store
  const { storeRateCardAssociationList } = useSelector(
    (state: RootState) => state.ratecard
  );
  const fdps = useSelector((state: RootState) => state.fdp.fdpListByClient);

  const storeOptions = storeRateCardAssociationList.map((store) => ({
    id: store.id,
    label: store.storeId,
    disabled: !store.isAssociated,
  }));

  const fdpOptions = fdps.map((fdp) => ({
    id: fdp.id,
    label: fdp.companyName,
  }));

  const validationSchema = Yup.object().shape({
    invoiceId: Yup.string().required("Invoice ID is required"),
    associatedStores: Yup.array().min(1, "Select at least one store"),
    startDate: Yup.date().nullable().required("Start date is required"),
    endDate: Yup.date()
      .nullable()
      .required("End date is required")
      .min(Yup.ref("startDate"), "End date must be after start date "),
    submitDate: Yup.date().nullable().required("Submit date is required"),
    associatedFdps: Yup.array().min(1, "Select at least one FDP"),
    netPayment: Yup.number().required("Net Payment is required").min(0, "Must be a positive value"),
  });

  const doUpload = async (uiRequest: ManualUploadSubmittedInvoiceUiRequest, invoicesToDelete: SubmittedClientInvoice[] = []) => {
    if (!uiRequest.file) {
      toast.error("Please select a file", { toastId: "noFileSelected" });
      return false;
    }

    const invoiceData = {
      clientInvoiceId: uiRequest.invoiceId,
      clientId: props.clientId,
      associatedStores: uiRequest.associatedStores,
      startDate: uiRequest.startDate!.format("YYYY-MM-DD"),
      endDate: uiRequest.endDate!.format("YYYY-MM-DD"),
      submitDate: uiRequest.submitDate!.format("YYYY-MM-DD"),
      associatedFdps: uiRequest.associatedFdps,
      netPayment: uiRequest.netPayment,
    };

    const formData = new FormData();
    formData.append("file", uiRequest.file);
    formData.append("invoiceData", new Blob([JSON.stringify(invoiceData)], { type: "application/json" }));

    try {
      const result = await ClientInvoiceAgent.uploadManualSubmittedInvoice(formData);
      if (!result.isSuccess) {
        setValidationErrorMsg(result.message ?? "Upload failed.");
        return false;
      }
      toast.success("Invoice uploaded successfully", { toastId: "uploadSubmittedInvoiceSuccess" });
      props.onCompleted();
      return true;
    } catch (error: any) {
      const msg = error?.message?.message || error?.message || "Upload failed.";
      setValidationErrorMsg(msg+" Please delete the existing invoice before submitting a new one.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (
    uiRequest: ManualUploadSubmittedInvoiceUiRequest
  ) => {
    try {
      const response = await ClientInvoiceAgent.validateInvoiceDetailsSubmitInternal(
        props.clientId,
        uiRequest.associatedStores,
        uiRequest.startDate!.format("YYYY-MM-DD"),
        uiRequest.endDate!.format("YYYY-MM-DD")
      );
      if (response.data === true) {
        const existing = await ClientInvoiceAgent.getClientSubmittedInvoiceList(props.clientId, { pageIndex: 0, pageSize: 1000 });
        const matched = existing.data.content.filter((inv) => {
          let stores: string[] = [];
          try {
            stores = JSON.parse(inv.associatedStores);
          } catch {
            stores = inv.associatedStores.split(",").map((s) => s.trim());
          }
          return (
            stores.some((s) => uiRequest.associatedStores.includes(s)) &&
            inv.startDate === uiRequest.startDate!.format("YYYY-MM-DD") &&
            inv.endDate === uiRequest.endDate!.format("YYYY-MM-DD")
          );
        });
        setExistingInvoices(matched);
        setPendingValues(uiRequest);
        setOverrideDialogOpen(true);
        setIsLoading(false);
        return;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      setValidationErrorMsg(appError.errorMessage);
      setIsLoading(false);
      return;
    }
    await doUpload(uiRequest);
  };

  return (
    <>
      <Formik
        initialValues={{ ...initialValues }}
        validationSchema={validationSchema}
        onSubmit={(
          values: ManualUploadSubmittedInvoiceUiRequest,
          { setSubmitting }
        ) => {
          if (!values.file) {
            setFileError(true);
            setSubmitting(false);
            return;
          }
          setFileError(false);
          setSubmitting(true);
          setIsLoading(true);
          handleSubmit(values).finally(() => {
            setSubmitting(false);
            setIsLoading(false);
          });
        }}
      >
        {({ values, setFieldValue, isSubmitting, errors, touched }) => {
          return (
            <SubmitModalWithFormik
              title="Upload Manual Invoice"
              closeSubmitModal={props.closeManualUploadSubmittedInvoiceModal}
              childErrorMessage={""}
              allowSubmit={true}
              size="xs"
              isLoading={isLoading}
              submitButtonName="Upload"
              closeModalAfterSubmit={false}
              fullWidth={false}
              allowSubmitWithoutTouch={true}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Loader isLoading={isSubmitting || isLoading} />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        fullWidth
                        sx={{ mt: 2 }}
                      >
                        {selectedFileName || "Select Invoice File (Excel/Csv)"}
                        <input
                          type="file"
                          hidden
                          accept=".xlsx,.xls,.csv"
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) {
                              setFieldValue("file", file);
                              setSelectedFileName(file.name);
                              setFileError(false);
                            }
                          }}
                        />
                      </Button>
                      {fileError && (
                        <Box sx={{ color: "error.main", fontSize: "0.75rem", mt: 1 }}>
                          Please select a file to upload
                        </Box>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <FormikTextField
                        name="invoiceId"
                        label="Invoice ID"
                        size="small"
                        fullWidth
                        required
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormikTextField
                        name="status"
                        label="Status"
                        size="small"
                        fullWidth
                        disabled
                        variant="outlined"
                        helperText="Default: Client Invoiced"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        id="associatedStores"
                        options={storeOptions}
                        size="small"
                        getOptionLabel={(option) => option.label}
                        getOptionDisabled={(option) => option.disabled}
                        value={storeOptions.filter((opt) =>
                          values.associatedStores.includes(opt.label)
                        )}
                        onChange={(event, value) =>
                          setFieldValue(
                            "associatedStores",
                            value.map((v) => v.label)
                          )
                        }
                        renderInput={(params) => (
                          <FormikTextField
                            {...params}
                            name="associatedStores"
                            label="Associated Stores"
                            required
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Start Date"
                          value={values.startDate}
                          onChange={(newValue) =>
                            setFieldValue("startDate", newValue)
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              required: true,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="End Date"
                          value={values.endDate}
                          onChange={(newValue) =>
                            setFieldValue("endDate", newValue)
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              required: true,
                              error: touched.endDate && !!errors.endDate,
                              helperText: touched.endDate && errors.endDate ? String(errors.endDate) : "",
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Submit Date"
                          value={values.submitDate}
                          onChange={(newValue) =>
                            setFieldValue("submitDate", newValue)
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              required: true,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12}>
                      <FormikTextField
                        name="netPayment"
                        label="Net Payment"
                        size="small"
                        fullWidth
                        required
                        variant="outlined"
                        type="number"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        id="associatedFdps"
                        options={fdpOptions}
                        size="small"
                        getOptionLabel={(option) => option.label}
                        value={fdpOptions.filter((opt) =>
                          values.associatedFdps.includes(opt.label)
                        )}
                        onChange={(event, value) =>
                          setFieldValue(
                            "associatedFdps",
                            value.map((v) => v.label)
                          )
                        }
                        renderInput={(params) => (
                          <FormikTextField
                            {...params}
                            name="associatedFdps"
                            label="Associated FDPs"
                            required
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
              </Box>
            </SubmitModalWithFormik>
          );
        }}
      </Formik>

      <Dialog open={!!validationErrorMsg} onClose={() => setValidationErrorMsg("")}>
        <DialogContent>
          <DialogContentText>{validationErrorMsg}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => {
            setValidationErrorMsg("");
          }}>Ok</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={overrideDialogOpen} onClose={() => setOverrideDialogOpen(false)}>
        <DialogContent>
          <DialogContentText>
            An invoice already exists for the selected Stores and Date Range. Please delete the existing invoice before submitting a new one.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOverrideDialogOpen(false);
            setPendingValues(null);
          }}>Ok</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManualUploadSubmittedInvoiceModal;
